
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";

interface AudienceInsightsProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

// Sample data
const locationData = [
  { name: "United States", value: 35 },
  { name: "United Kingdom", value: 18 },
  { name: "Canada", value: 12 },
  { name: "Australia", value: 8 },
  { name: "Germany", value: 7 },
  { name: "France", value: 5 },
  { name: "Other", value: 15 }
];

const ageData = [
  { name: "18-24", value: 15 },
  { name: "25-34", value: 32 },
  { name: "35-44", value: 27 },
  { name: "45-54", value: 14 },
  { name: "55-64", value: 8 },
  { name: "65+", value: 4 }
];

const deviceData = [
  { name: "Mobile", value: 58 },
  { name: "Desktop", value: 34 },
  { name: "Tablet", value: 8 }
];

const topicsData = [
  { name: "Technology", reads: 4250 },
  { name: "Politics", reads: 3780 },
  { name: "Health", reads: 3120 },
  { name: "Environment", reads: 2890 },
  { name: "Business", reads: 2560 },
  { name: "Science", reads: 2340 },
  { name: "Culture", reads: 1980 },
  { name: "Sports", reads: 1760 }
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const AudienceInsights: React.FC<AudienceInsightsProps> = ({ timeframe }) => {
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Geographic Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              location: { 
                theme: { light: '#F97316', dark: '#F97316' } 
              }
            }}
            className="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={locationData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#F97316"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {locationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="font-medium">{payload[0].name}</div>
                            <div className="font-mono text-right">{payload[0].value}%</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Age Demographics</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              age: { 
                theme: { light: '#2563EB', dark: '#2563EB' } 
              }
            }}
            className="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="font-medium">{label}</div>
                            <div className="font-mono text-right">{payload[0].value}%</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="value" fill="var(--color-age, #2563EB)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Device Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              device: { 
                theme: { light: '#10B981', dark: '#10B981' } 
              }
            }}
            className="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={deviceData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#10B981"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="font-medium">{payload[0].name}</div>
                            <div className="font-mono text-right">{payload[0].value}%</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Popular Topics</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              reads: { 
                theme: { light: '#F97316', dark: '#F97316' } 
              }
            }}
            className="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topicsData} layout="vertical">
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={100} />
                <Tooltip
                  formatter={(value) => [`${value.toLocaleString()}`, 'Reads']}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="font-medium">{payload[0].name}</div>
                            <div className="font-mono text-right">{payload[0].value.toLocaleString()}</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="reads" fill="var(--color-reads, #F97316)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default AudienceInsights;
